import React, {
	type SyntheticEvent,
	type KeyboardEvent,
	type DragEvent,
	useCallback,
	useMemo,
	useState,
	type ReactNode,
} from 'react';
import { styled } from '@compiled/react';
import debounce from 'lodash/debounce';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';

type Props = {
	children: ReactNode;
	onReceiveFile: (file: File) => Promise<void>;
	onClick: () => void;
	isDisabled: boolean;
	isCompact?: boolean;
	className?: string;
};

const cancelEvent = (event: SyntheticEvent<HTMLElement>) => {
	event.preventDefault();
	event.stopPropagation();
};

export const FileDropTarget = ({
	children,
	onReceiveFile,
	onClick,
	isDisabled,
	isCompact,
	className,
}: Props) => {
	const [isDraggedOver, setIsDraggedOver] = useState(false);

	const debouncedSetIsDraggedOver = useMemo(
		() => debounce((newValue: boolean) => setIsDraggedOver(newValue), 10),
		[setIsDraggedOver],
	);

	const activateDropZone = useCallback(() => {
		debouncedSetIsDraggedOver.cancel();
		setIsDraggedOver(true);
	}, [debouncedSetIsDraggedOver]);

	const deactivateDropzone = useCallback(() => {
		debouncedSetIsDraggedOver(false);
	}, [debouncedSetIsDraggedOver]);

	const onDragOverHandler = useCallback(
		(event: SyntheticEvent<HTMLElement>) => {
			cancelEvent(event);
			if (!isDisabled) {
				activateDropZone();
			}
		},
		[activateDropZone, isDisabled],
	);

	const onDragLeaveHandler = useCallback(
		(event: SyntheticEvent<HTMLElement>) => {
			cancelEvent(event);
			deactivateDropzone();
		},
		[deactivateDropzone],
	);

	const onDropHandler = useCallback(
		(event: DragEvent<HTMLDivElement>) => {
			cancelEvent(event);
			deactivateDropzone();
			if (!isDisabled && event.dataTransfer) {
				onReceiveFile(event.dataTransfer.files[0]);
			}
		},
		[deactivateDropzone, isDisabled, onReceiveFile],
	);

	const onClickHandler = useCallback(() => {
		if (!isDisabled) {
			onClick();
		}
	}, [isDisabled, onClick]);

	const onKeyDownHandler = useCallback(
		(event: KeyboardEvent<HTMLElement>) => {
			if (!isDisabled && event.key === 'Enter') {
				onClick();
			}
		},
		[isDisabled, onClick],
	);

	return (
		<DropzoneTarget
			data-testid="project-create-importer-upload-flow.common.ui.file-dropzone.file-drop-target.dropzone-target"
			isDraggedOver={isDraggedOver}
			onDragOver={onDragOverHandler}
			onDragLeave={onDragLeaveHandler}
			onDrop={onDropHandler}
			onClick={onClickHandler}
			onKeyDown={onKeyDownHandler}
			tabIndex={0}
			isCompact={isCompact}
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
			className={className}
		>
			{children}
		</DropzoneTarget>
	);
};

type DropzoneTargetProps = {
	isDraggedOver: boolean;
	isCompact?: boolean;
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const DropzoneTarget = styled.div<DropzoneTargetProps>({
	display: 'flex',
	position: 'relative',
	boxSizing: 'border-box',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	backgroundColor: token('elevation.surface', colors.N0),
	padding: token('space.100', '8px'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	minHeight: ({ isCompact }) => (isCompact ? '57px' : '184px'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	marginTop: ({ isCompact }) => (isCompact ? 0 : token('space.400', '32px')),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	marginBottom: ({ isCompact }) => (isCompact ? 0 : token('space.300', '24px')),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	':hover': {
		cursor: 'pointer',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	border: ({ isDraggedOver, isCompact }) =>
		`${isCompact ? '1px' : '2px'} dashed ${
			isDraggedOver
				? // eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
					token('color.border.selected', colors.B200)
				: // eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
					token('color.border', colors.N40)
		}`,
});
